/**
 * Run a screen's writes one after another, and when one fails, say exactly
 * which earlier ones were already saved.
 *
 * The rentals routes have no transactions, no DELETE for an inspection or a
 * rental period, and no way to reopen a closed period (#815). So a screen that
 * makes several writes cannot undo the ones that landed before a failure. What
 * it can do is order them so the billing write goes LAST (a failure before it
 * leaves billing untouched) and then tell the reader the truth: what failed,
 * the API's own reason, and what is already saved. Never a success toast, and
 * never a generic "something went wrong" that hides a half-written record.
 */

export interface WriteStep {
  /** What the step saves, as the reader would say it: "the pre-delivery inspection". */
  label: string;
  run: () => Promise<unknown>;
}

/**
 * The API's own sentence for a failed request (`{ success: false, error }`),
 * or the transport's message when there is no response at all. Axios's own
 * message ("Request failed with status code 400") is the last resort, because
 * it names nothing the reader can act on.
 */
export function apiErrorText(err: unknown): string {
  const e = err as { response?: { data?: { error?: unknown } }; message?: unknown } | null;
  const fromApi = e?.response?.data?.error;
  if (typeof fromApi === 'string' && fromApi.trim()) return fromApi;
  if (typeof e?.message === 'string' && e.message.trim()) return e.message;
  return 'the server gave no reason';
}

export class PartialWriteError extends Error {
  readonly failed: string;
  readonly written: string[];
  readonly reason: string;

  constructor(failed: string, written: string[], reason: string) {
    const saved = written.length === 0 ? 'Nothing was saved.' : `Already saved: ${written.join('; ')}.`;
    // The API's own sentences usually end in a full stop; this one supplies its own.
    super(`Could not save ${failed}: ${reason.replace(/[.\s]+$/, '')}. ${saved}`);
    this.name = 'PartialWriteError';
    this.failed = failed;
    this.written = written;
    this.reason = reason;
  }
}

/**
 * Runs each step in order; stops at the first failure and throws a
 * PartialWriteError.
 *
 * Pass `done` to make a retry safe: a step whose label is already in the set
 * is skipped rather than written a second time, and each step that succeeds
 * is added to it. Nothing here can be deleted afterwards, so re-sending an
 * inspection that already landed would leave two. Labels must therefore name
 * the record, not the kind ("the delivery inspection for FT-1").
 */
export async function runWriteSteps(steps: WriteStep[], done?: Set<string>): Promise<void> {
  const written: string[] = done ? steps.filter((s) => done.has(s.label)).map((s) => s.label) : [];
  for (const step of steps) {
    if (done?.has(step.label)) continue;
    try {
      await step.run();
    } catch (err) {
      throw new PartialWriteError(step.label, written, apiErrorText(err));
    }
    written.push(step.label);
    done?.add(step.label);
  }
}
